'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumb from '@/components/Breadcrumb';
import { createClient } from '@/lib/supabase/client';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface FlaggedItem {
  id: string;
  content_type: string;
  content_id: string;
  content_preview?: string;
  reason: string;
  status: string;
  admin_note?: string;
  created_at: string;
}

interface ModerationLogItem {
  id: string;
  admin_id?: string;
  action: string;
  target_type: string;
  target_id: string;
  target_preview?: string;
  note?: string;
  created_at: string;
}

interface CommunityShare {
  id: string;
  content_type: string;
  content_label: string;
  caption?: string;
  role: string;
  is_featured: boolean;
  is_hidden: boolean;
  trending_score: number;
  created_at: string;
}

interface ResonanceRow {
  content_type: string;
  content_id: string;
  content_title: string;
  count: number;
}

interface WalkthroughRow {
  checkpoint_id: string;
  checkpoint_title: string;
  checkpoint_index: number;
  count: number;
}

interface RoleRow {
  caretaker_role: string;
  count: number;
}

const ADMIN_EMAILS = ['admin@haalproject.com'];

type AdminTab = 'moderation' | 'analytics' | 'shares' | 'log';

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function AdminModerationPage() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [activeTab, setActiveTab] = useState<AdminTab>('moderation');

  // Moderation state
  const [flaggedItems, setFlaggedItems] = useState<FlaggedItem[]>([]);
  const [moderationLog, setModerationLog] = useState<ModerationLogItem[]>([]);
  const [communityShares, setCommunityShares] = useState<CommunityShare[]>([]);
  const [actionNote, setActionNote] = useState<Record<string, string>>({});
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Analytics state
  const [topFrameworks, setTopFrameworks] = useState<ResonanceRow[]>([]);
  const [topVerses, setTopVerses] = useState<ResonanceRow[]>([]);
  const [topKeys, setTopKeys] = useState<ResonanceRow[]>([]);
  const [walkthroughData, setWalkthroughData] = useState<WalkthroughRow[]>([]);
  const [roleData, setRoleData] = useState<RoleRow[]>([]);
  const [totalBookmarks, setTotalBookmarks] = useState(0);
  const [totalResonance, setTotalResonance] = useState(0);
  const [totalWalkthrough, setTotalWalkthrough] = useState(0);

  const supabase = createClient();

  useEffect(() => {
    supabase.auth.getUser().then(async ({ data: { user } }) => {
      setUser(user);
      if (user && ADMIN_EMAILS.includes(user.email || '')) {
        setAuthorized(true);
        await Promise.all([loadModeration(), loadAnalytics()]);
      }
      setLoading(false);
    });
  }, []);

  const loadModeration = useCallback(async () => {
    const [{ data: flagged }, { data: log }, { data: shares }] = await Promise.all([
      supabase.from('flagged_content').select('*').order('created_at', { ascending: false }).limit(50),
      supabase.from('moderation_log').select('*').order('created_at', { ascending: false }).limit(50),
      supabase.from('community_shares').select('*').order('created_at', { ascending: false }).limit(30),
    ]);
    setFlaggedItems(flagged || []);
    setModerationLog(log || []);
    setCommunityShares(shares || []);
  }, [supabase]);

  async function loadAnalytics() {
    const { data: frameworkData } = await supabase.from('content_resonance').select('content_type, content_id, content_title').eq('content_type', 'framework_page');
    if (frameworkData) { const counts = countBy(frameworkData, 'content_id'); setTopFrameworks(topN(frameworkData, counts, 10)); }

    const { data: verseData } = await supabase.from('content_resonance').select('content_type, content_id, content_title').eq('resonance_type', 'verse_click');
    if (verseData) { const counts = countBy(verseData, 'content_id'); setTopVerses(topN(verseData, counts, 10)); }

    const { data: keyData } = await supabase.from('content_resonance').select('content_type, content_id, content_title').eq('content_type', 'key_concept');
    if (keyData) { const counts = countBy(keyData, 'content_id'); setTopKeys(topN(keyData, counts, 10)); }

    const { data: wtData } = await supabase.from('walkthrough_completions').select('checkpoint_id, checkpoint_title, checkpoint_index');
    if (wtData) {
      const counts = countBy(wtData, 'checkpoint_id');
      const unique = Object.entries(counts).map(([id, count]) => {
        const row = wtData.find((r: any) => r.checkpoint_id === id)!;
        return { checkpoint_id: id, checkpoint_title: row.checkpoint_title, checkpoint_index: row.checkpoint_index, count };
      }).sort((a, b) => a.checkpoint_index - b.checkpoint_index);
      setWalkthroughData(unique);
      setTotalWalkthrough(wtData.length);
    }

    const { data: roleRows } = await supabase.from('content_resonance').select('caretaker_role').neq('caretaker_role', '');
    if (roleRows) {
      const counts = countBy(roleRows, 'caretaker_role');
      setRoleData(Object.entries(counts).map(([role, count]) => ({ caretaker_role: role, count })).sort((a, b) => b.count - a.count));
    }

    const { count: bCount } = await supabase.from('bookmarks').select('*', { count: 'exact', head: true });
    setTotalBookmarks(bCount || 0);
    const { count: rCount } = await supabase.from('content_resonance').select('*', { count: 'exact', head: true });
    setTotalResonance(rCount || 0);
  }

  function countBy(arr: any[], key: string): Record<string, number> {
    return arr.reduce((acc, row) => { acc[row[key]] = (acc[row[key]] || 0) + 1; return acc; }, {});
  }

  function topN(arr: any[], counts: Record<string, number>, n: number): ResonanceRow[] {
    const seen = new Set<string>();
    const unique: ResonanceRow[] = [];
    for (const row of arr) {
      if (!seen.has(row.content_id)) {
        seen.add(row.content_id);
        unique.push({ content_type: row.content_type, content_id: row.content_id, content_title: row.content_title, count: counts[row.content_id] });
      }
    }
    return unique.sort((a, b) => b.count - a.count).slice(0, n);
  }

  async function logAction(action: string, targetType: string, targetId: string, targetPreview?: string, note?: string) {
    await supabase.from('moderation_log').insert({
      admin_id: user?.id,
      action,
      target_type: targetType,
      target_id: targetId,
      target_preview: targetPreview,
      note,
    });
  }

  async function handleFlag(item: FlaggedItem, action: 'reviewed' | 'actioned' | 'dismissed') {
    setActionLoading(item.id);
    await supabase.from('flagged_content').update({
      status: action,
      admin_note: actionNote[item.id] || null,
      actioned_by: user?.id,
      actioned_at: new Date().toISOString(),
    }).eq('id', item.id);
    await logAction(action, item.content_type, item.content_id, item.content_preview, actionNote[item.id]);
    await loadModeration();
    setActionLoading(null);
  }

  async function handleShare(share: CommunityShare, action: 'feature' | 'hide' | 'unhide' | 'unfeature') {
    setActionLoading(share.id);
    const updates: any = {};
    if (action === 'feature') updates.is_featured = true;
    if (action === 'unfeature') updates.is_featured = false;
    if (action === 'hide') updates.is_hidden = true;
    if (action === 'unhide') updates.is_hidden = false;
    await supabase.from('community_shares').update(updates).eq('id', share.id);
    await logAction(action, 'share', share.id, share.content_label);
    await loadModeration();
    setActionLoading(null);
  }

  const maxWalkthrough = Math.max(...walkthroughData.map(w => w.count), 1);
  const completionRate = walkthroughData.length > 0
    ? Math.round((walkthroughData[walkthroughData.length - 1]?.count / walkthroughData[0]?.count) * 100) || 0
    : 0;

  const pendingFlags = flaggedItems.filter(f => f.status === 'pending');
  const reviewedFlags = flaggedItems.filter(f => f.status !== 'pending');

  const TABS: { id: AdminTab; label: string; icon: string; badge?: number }[] = [
    { id: 'moderation', label: 'Flagged Queue', icon: '⚑', badge: pendingFlags.length },
    { id: 'shares', label: 'Community Shares', icon: '⬡' },
    { id: 'analytics', label: 'Analytics', icon: '◈' },
    { id: 'log', label: 'Moderation Log', icon: '◎' },
  ];

  if (loading) return (
    <div style={{ background: '#060d12', minHeight: '100vh' }}>
      <Header />
      <Breadcrumb items={[{ label: 'Admin', href: '/admin-dashboard' }, { label: 'Moderation' }]} />
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="font-mono text-xs uppercase tracking-widest" style={{ color: 'rgba(201,168,76,0.4)' }}>Loading…</div>
      </div>
    </div>
  );

  if (!user || !authorized) return (
    <div style={{ background: '#060d12', minHeight: '100vh' }}>
      <Header />
      <Breadcrumb items={[{ label: 'Admin', href: '/admin-dashboard' }, { label: 'Moderation' }]} />
      <div className="max-w-lg mx-auto px-6 py-24 text-center">
        <div className="font-mono text-xs uppercase tracking-widest mb-4" style={{ color: 'rgba(201,168,76,0.4)' }}>Admin Access Required</div>
        <h1 className="font-mono text-2xl uppercase tracking-tight mb-4" style={{ color: '#F5F0E8' }}>Moderation</h1>
        <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 13, marginBottom: 20 }}>This area is restricted to project administrators.</p>
        <Link href="/" className="font-mono text-xs uppercase tracking-widest px-4 py-2" style={{ border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c' }}>← Return Home</Link>
      </div>
      <Footer />
    </div>
  );

  return (
    <div style={{ background: '#060d12', minHeight: '100vh' }}>
      <Header />
      <Breadcrumb items={[{ label: 'Admin', href: '/admin-dashboard' }, { label: 'Moderation' }]} />
      <main className="max-w-6xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-8">
          <p className="font-mono text-xs tracking-widest uppercase mb-2" style={{ color: 'rgba(201,168,76,0.5)' }}>✦ Admin</p>
          <h1 className="font-mono text-3xl font-black uppercase tracking-tight mb-2" style={{ color: '#F5F0E8' }}>Moderation Center</h1>
          <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 13, fontStyle: 'italic' }}>Tend the community garden. Feature what flourishes. Guard what matters.</p>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Pending Flags', value: pendingFlags.length, color: pendingFlags.length > 0 ? '#D4A82A' : '#2d7a47', icon: '⚑' },
            { label: 'Total Shares', value: communityShares.length, color: '#2d8a87', icon: '⬡' },
            { label: 'Featured', value: communityShares.filter(s => s.is_featured).length, color: '#c9a84c', icon: '✦' },
            { label: 'Actions Taken', value: moderationLog.length, color: '#8a8ab8', icon: '◎' },
          ].map((stat, i) => (
            <div key={i} className="rounded-xl p-5" style={{ background: `${stat.color}06`, border: `1px solid ${stat.color}20` }}>
              <div style={{ fontSize: 22, color: stat.color, marginBottom: 6 }}>{stat.icon}</div>
              <div className="font-mono text-2xl font-black" style={{ color: stat.color }}>{stat.value}</div>
              <div className="font-mono text-[9px] uppercase tracking-widest mt-1" style={{ color: 'rgba(212,197,169,0.4)' }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 flex-wrap mb-8 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest px-4 py-3 transition-all duration-200 relative"
              style={{
                color: activeTab === tab.id ? '#c9a84c' : 'rgba(212,197,169,0.4)',
                borderBottom: activeTab === tab.id ? '2px solid #c9a84c' : '2px solid transparent',
                marginBottom: -1,
              }}
            >
              <span>{tab.icon}</span>
              {tab.label}
              {tab.badge !== undefined && tab.badge > 0 && (
                <span className="font-mono text-[8px] px-1.5 py-0.5 rounded-full" style={{ background: '#D4A82A', color: '#060d12' }}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── FLAGGED QUEUE ── */}
        {activeTab === 'moderation' && (
          <div className="space-y-6">
            {pendingFlags.length === 0 ? (
              <div className="rounded-xl p-12 text-center" style={{ background: 'rgba(45,122,71,0.04)', border: '1px solid rgba(45,122,71,0.15)' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
                <p className="font-mono text-sm uppercase tracking-widest" style={{ color: '#2d7a47' }}>Queue Clear</p>
                <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 13, marginTop: 8 }}>No pending flags. Alhamdulilah.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.4)' }}>
                  {pendingFlags.length} item{pendingFlags.length > 1 ? 's' : ''} awaiting review
                </p>
                {pendingFlags.map(item => (
                  <FlaggedCard
                    key={item.id}
                    item={item}
                    note={actionNote[item.id] || ''}
                    onNoteChange={v => setActionNote(prev => ({ ...prev, [item.id]: v }))}
                    onAction={action => handleFlag(item, action)}
                    loading={actionLoading === item.id}
                  />
                ))}
              </div>
            )}

            {reviewedFlags.length > 0 && (
              <div>
                <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(212,197,169,0.3)' }}>Previously Reviewed</p>
                <div className="space-y-2">
                  {reviewedFlags.slice(0, 10).map(item => (
                    <div key={item.id} className="flex items-center gap-3 px-4 py-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.04)', opacity: 0.6 }}>
                      <StatusBadge status={item.status} />
                      <span className="font-mono text-[10px] uppercase tracking-widest flex-1" style={{ color: 'rgba(212,197,169,0.5)' }}>{item.content_type} · {item.reason}</span>
                      <span className="font-mono text-[9px]" style={{ color: 'rgba(212,197,169,0.3)' }}>{new Date(item.created_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── COMMUNITY SHARES ── */}
        {activeTab === 'shares' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.4)' }}>
                {communityShares.length} total shares
              </p>
              <div className="flex gap-2">
                <span className="font-mono text-[8px] px-2 py-1 rounded" style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', color: '#c9a84c' }}>
                  ✦ {communityShares.filter(s => s.is_featured).length} featured
                </span>
                <span className="font-mono text-[8px] px-2 py-1 rounded" style={{ background: 'rgba(255,80,80,0.06)', border: '1px solid rgba(255,80,80,0.15)', color: 'rgba(255,120,120,0.7)' }}>
                  ⊘ {communityShares.filter(s => s.is_hidden).length} hidden
                </span>
              </div>
            </div>
            {communityShares.map(share => (
              <ShareModerationCard
                key={share.id}
                share={share}
                onAction={action => handleShare(share, action)}
                loading={actionLoading === share.id}
              />
            ))}
            {communityShares.length === 0 && (
              <div className="text-center py-12">
                <p style={{ color: 'rgba(212,197,169,0.3)', fontSize: 14 }}>No community shares yet.</p>
              </div>
            )}
          </div>
        )}

        {/* ── ANALYTICS ── */}
        {activeTab === 'analytics' && (
          <div className="space-y-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Total Bookmarks', value: totalBookmarks, color: '#c9a84c', icon: '◈' },
                { label: 'Resonance Events', value: totalResonance, color: '#2d8a87', icon: '⬡' },
                { label: 'Walkthrough Steps', value: totalWalkthrough, color: '#2d7a47', icon: '◎' },
                { label: 'Completion Rate', value: `${completionRate}%`, color: '#8a8ab8', icon: '✦' },
              ].map((stat, i) => (
                <div key={i} className="rounded-xl p-5" style={{ background: `${stat.color}06`, border: `1px solid ${stat.color}20` }}>
                  <div style={{ fontSize: 22, color: stat.color, marginBottom: 6 }}>{stat.icon}</div>
                  <div className="font-mono text-2xl font-black" style={{ color: stat.color }}>{stat.value}</div>
                  <div className="font-mono text-[9px] uppercase tracking-widest mt-1" style={{ color: 'rgba(212,197,169,0.4)' }}>{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <AnalyticsCard title="Framework Pages" subtitle="Most visited sections" color="#2d8a87" icon="⬡" rows={topFrameworks} />
              <AnalyticsCard title="Key Concepts" subtitle="Most resonant Keys" color="#d4a82a" icon="⌘" rows={topKeys} />
            </div>

            <AnalyticsCard title="Verse Links" subtitle="Most clicked Quranic references" color="#2d7a47" icon="☽" rows={topVerses} wide />

            {/* Walkthrough Funnel */}
            <div className="rounded-xl p-6" style={{ background: 'rgba(45,138,135,0.04)', border: '1px solid rgba(45,138,135,0.15)' }}>
              <div className="flex items-center gap-2 mb-6">
                <span style={{ color: '#2d8a87', fontSize: 18 }}>◎</span>
                <div>
                  <h2 className="font-mono text-sm font-black uppercase tracking-wide" style={{ color: '#F5F0E8' }}>Guided Walkthrough Funnel</h2>
                  <p className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.4)' }}>Checkpoint completion rates</p>
                </div>
              </div>
              {walkthroughData.length === 0 ? (
                <p className="font-mono text-xs" style={{ color: 'rgba(212,197,169,0.3)' }}>No walkthrough data yet.</p>
              ) : (
                <div className="space-y-3">
                  {walkthroughData.map((w, i) => (
                    <div key={w.checkpoint_id} className="flex items-center gap-3">
                      <div className="font-mono text-[9px] w-4 text-right" style={{ color: 'rgba(212,197,169,0.4)' }}>{i + 1}</div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: '#d4c5a9' }}>{w.checkpoint_title}</span>
                          <span className="font-mono text-[9px]" style={{ color: 'rgba(212,197,169,0.4)' }}>{w.count}</span>
                        </div>
                        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(45,138,135,0.1)' }}>
                          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(w.count / maxWalkthrough) * 100}%`, background: i === walkthroughData.length - 1 ? '#2d7a47' : '#2d8a87' }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Role Distribution */}
            <div className="rounded-xl p-6" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.15)' }}>
              <div className="flex items-center gap-2 mb-6">
                <span style={{ color: '#c9a84c', fontSize: 18 }}>◈</span>
                <div>
                  <h2 className="font-mono text-sm font-black uppercase tracking-wide" style={{ color: '#F5F0E8' }}>Caretaker Role Distribution</h2>
                  <p className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.4)' }}>Who is engaging with the platform</p>
                </div>
              </div>
              {roleData.length === 0 ? (
                <p className="font-mono text-xs" style={{ color: 'rgba(212,197,169,0.3)' }}>No role data yet.</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {roleData.map(r => (
                    <div key={r.caretaker_role} className="rounded-lg p-3 text-center" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)' }}>
                      <div className="font-mono text-xl font-black" style={{ color: '#c9a84c' }}>{r.count}</div>
                      <div className="font-mono text-[9px] uppercase tracking-widest mt-1" style={{ color: 'rgba(212,197,169,0.5)' }}>{r.caretaker_role.replace('-', ' ')}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── MODERATION LOG ── */}
        {activeTab === 'log' && (
          <div className="space-y-3">
            <p className="font-mono text-[9px] uppercase tracking-widest mb-4" style={{ color: 'rgba(212,197,169,0.4)' }}>
              {moderationLog.length} actions recorded
            </p>
            {moderationLog.length === 0 ? (
              <div className="text-center py-12">
                <p style={{ color: 'rgba(212,197,169,0.3)', fontSize: 14 }}>No moderation actions yet.</p>
              </div>
            ) : (
              moderationLog.map(entry => (
                <div key={entry.id} className="flex items-start gap-4 px-4 py-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <ActionBadge action={entry.action} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[10px] font-bold uppercase tracking-wide" style={{ color: '#F5F0E8' }}>{entry.action}</span>
                      <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.4)' }}>{entry.target_type}</span>
                    </div>
                    {entry.target_preview && (
                      <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, marginTop: 2 }} className="truncate">{entry.target_preview}</p>
                    )}
                    {entry.note && (
                      <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 11, fontStyle: 'italic', marginTop: 2 }}>"{entry.note}"</p>
                    )}
                  </div>
                  <span className="font-mono text-[9px] flex-shrink-0" style={{ color: 'rgba(212,197,169,0.3)' }}>
                    {new Date(entry.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))
            )}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

// ─── FLAGGED CARD ─────────────────────────────────────────────────────────────

function FlaggedCard({ item, note, onNoteChange, onAction, loading }: {
  item: FlaggedItem;
  note: string;
  onNoteChange: (v: string) => void;
  onAction: (action: 'reviewed' | 'actioned' | 'dismissed') => void;
  loading: boolean;
}) {
  return (
    <div className="rounded-xl p-5" style={{ background: 'rgba(212,168,42,0.04)', border: '1px solid rgba(212,168,42,0.15)' }}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded" style={{ background: 'rgba(212,168,42,0.1)', border: '1px solid rgba(212,168,42,0.25)', color: '#D4A82A' }}>
              {item.content_type}
            </span>
            <StatusBadge status={item.status} />
          </div>
          <p className="font-mono text-xs font-bold" style={{ color: '#F5F0E8' }}>{item.reason}</p>
          {item.content_preview && (
            <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>{item.content_preview}</p>
          )}
        </div>
        <span className="font-mono text-[9px] flex-shrink-0" style={{ color: 'rgba(212,197,169,0.3)' }}>
          {new Date(item.created_at).toLocaleDateString()}
        </span>
      </div>

      <textarea
        value={note}
        onChange={e => onNoteChange(e.target.value)}
        placeholder="Admin note (optional)..."
        rows={2}
        className="w-full px-3 py-2 font-mono text-xs focus:outline-none rounded mb-3 resize-none"
        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(212,197,169,0.7)' }}
      />

      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => onAction('actioned')}
          disabled={loading}
          className="font-mono text-[9px] uppercase tracking-widest px-3 py-2 rounded transition-all"
          style={{ background: 'rgba(255,80,80,0.08)', border: '1px solid rgba(255,80,80,0.2)', color: 'rgba(255,120,120,0.8)', opacity: loading ? 0.5 : 1 }}
        >
          ⊘ Take Action
        </button>
        <button
          onClick={() => onAction('reviewed')}
          disabled={loading}
          className="font-mono text-[9px] uppercase tracking-widest px-3 py-2 rounded transition-all"
          style={{ background: 'rgba(45,138,135,0.08)', border: '1px solid rgba(45,138,135,0.2)', color: '#2d8a87', opacity: loading ? 0.5 : 1 }}
        >
          ◎ Mark Reviewed
        </button>
        <button
          onClick={() => onAction('dismissed')}
          disabled={loading}
          className="font-mono text-[9px] uppercase tracking-widest px-3 py-2 rounded transition-all"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(212,197,169,0.4)', opacity: loading ? 0.5 : 1 }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

// ─── SHARE MODERATION CARD ────────────────────────────────────────────────────

function ShareModerationCard({ share, onAction, loading }: {
  share: CommunityShare;
  onAction: (action: 'feature' | 'hide' | 'unhide' | 'unfeature') => void;
  loading: boolean;
}) {
  const TYPE_COLORS: Record<string, string> = { quest: '#D4A82A', garment: '#2d8a87', badge: '#8a8ab8' };
  const color = TYPE_COLORS[share.content_type] || '#c9a84c';

  return (
    <div
      className="rounded-xl p-4 flex items-start gap-4"
      style={{
        background: share.is_featured ? 'rgba(201,168,76,0.04)' : share.is_hidden ? 'rgba(255,80,80,0.03)' : 'rgba(255,255,255,0.02)',
        border: `1px solid ${share.is_featured ? 'rgba(201,168,76,0.2)' : share.is_hidden ? 'rgba(255,80,80,0.12)' : 'rgba(255,255,255,0.05)'}`,
        opacity: share.is_hidden ? 0.6 : 1,
      }}
    >
      <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm" style={{ background: `${color}15`, border: `1px solid ${color}30`, color }}>
        {share.content_type === 'quest' ? '⌘' : share.content_type === 'garment' ? '⬡' : '✦'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color }}>{share.content_type}</span>
          <span className="font-mono text-xs font-bold" style={{ color: '#F5F0E8' }}>{share.content_label}</span>
          {share.is_featured && <span className="font-mono text-[7px] uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: 'rgba(201,168,76,0.12)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.25)' }}>✦ Featured</span>}
          {share.is_hidden && <span className="font-mono text-[7px] uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,80,80,0.08)', color: 'rgba(255,120,120,0.7)', border: '1px solid rgba(255,80,80,0.15)' }}>⊘ Hidden</span>}
        </div>
        {share.caption && <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, lineHeight: 1.5 }}>{share.caption}</p>}
        <div className="flex items-center gap-3 mt-1">
          <span className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.3)' }}>{share.role.replace('-', ' ')}</span>
          <span className="font-mono text-[8px]" style={{ color: 'rgba(212,197,169,0.3)' }}>Score: {share.trending_score}</span>
        </div>
      </div>
      <div className="flex flex-col gap-1.5 flex-shrink-0">
        {!share.is_featured ? (
          <button onClick={() => onAction('feature')} disabled={loading}
            className="font-mono text-[8px] uppercase tracking-widest px-2 py-1.5 rounded transition-all"
            style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.2)', color: '#c9a84c', opacity: loading ? 0.5 : 1 }}>
            ✦ Feature
          </button>
        ) : (
          <button onClick={() => onAction('unfeature')} disabled={loading}
            className="font-mono text-[8px] uppercase tracking-widest px-2 py-1.5 rounded transition-all"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(212,197,169,0.4)', opacity: loading ? 0.5 : 1 }}>
            Unfeature
          </button>
        )}
        {!share.is_hidden ? (
          <button onClick={() => onAction('hide')} disabled={loading}
            className="font-mono text-[8px] uppercase tracking-widest px-2 py-1.5 rounded transition-all"
            style={{ background: 'rgba(255,80,80,0.06)', border: '1px solid rgba(255,80,80,0.15)', color: 'rgba(255,120,120,0.7)', opacity: loading ? 0.5 : 1 }}>
            ⊘ Hide
          </button>
        ) : (
          <button onClick={() => onAction('unhide')} disabled={loading}
            className="font-mono text-[8px] uppercase tracking-widest px-2 py-1.5 rounded transition-all"
            style={{ background: 'rgba(45,122,71,0.08)', border: '1px solid rgba(45,122,71,0.2)', color: '#2d7a47', opacity: loading ? 0.5 : 1 }}>
            Unhide
          </button>
        )}
      </div>
    </div>
  );
}

// ─── STATUS BADGE ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { color: string; bg: string }> = {
    pending: { color: '#D4A82A', bg: 'rgba(212,168,42,0.1)' },
    reviewed: { color: '#2d8a87', bg: 'rgba(45,138,135,0.1)' },
    actioned: { color: 'rgba(255,120,120,0.8)', bg: 'rgba(255,80,80,0.08)' },
    dismissed: { color: 'rgba(212,197,169,0.4)', bg: 'rgba(255,255,255,0.03)' },
  };
  const style = map[status] || map['pending'];
  return (
    <span className="font-mono text-[7px] uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: style.bg, color: style.color, border: `1px solid ${style.color}30` }}>
      {status}
    </span>
  );
}

// ─── ACTION BADGE ─────────────────────────────────────────────────────────────

function ActionBadge({ action }: { action: string }) {
  const map: Record<string, { color: string; icon: string }> = {
    feature: { color: '#c9a84c', icon: '✦' },
    unfeature: { color: 'rgba(212,197,169,0.4)', icon: '○' },
    hide: { color: 'rgba(255,120,120,0.7)', icon: '⊘' },
    unhide: { color: '#2d7a47', icon: '◎' },
    actioned: { color: 'rgba(255,120,120,0.7)', icon: '⚑' },
    reviewed: { color: '#2d8a87', icon: '◎' },
    dismissed: { color: 'rgba(212,197,169,0.4)', icon: '—' },
    approve: { color: '#2d7a47', icon: '✓' },
    reject: { color: 'rgba(255,120,120,0.7)', icon: '✗' },
  };
  const style = map[action] || { color: '#c9a84c', icon: '◈' };
  return (
    <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs flex-shrink-0" style={{ background: `${style.color}15`, border: `1px solid ${style.color}30`, color: style.color }}>
      {style.icon}
    </div>
  );
}

// ─── ANALYTICS CARD ───────────────────────────────────────────────────────────

function AnalyticsCard({ title, subtitle, color, icon, rows, wide = false }: {
  title: string; subtitle: string; color: string; icon: string;
  rows: { content_id: string; content_title: string; count: number }[];
  wide?: boolean;
}) {
  const max = Math.max(...rows.map(r => r.count), 1);
  return (
    <div className="rounded-xl p-6" style={{ background: `${color}04`, border: `1px solid ${color}15` }}>
      <div className="flex items-center gap-2 mb-5">
        <span style={{ color, fontSize: 18 }}>{icon}</span>
        <div>
          <h2 className="font-mono text-sm font-black uppercase tracking-wide" style={{ color: '#F5F0E8' }}>{title}</h2>
          <p className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.4)' }}>{subtitle}</p>
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="font-mono text-xs" style={{ color: 'rgba(212,197,169,0.3)' }}>No data yet.</p>
      ) : (
        <div className={`space-y-2 ${wide ? 'grid md:grid-cols-2 gap-2 space-y-0' : ''}`}>
          {rows.map((r, i) => (
            <div key={r.content_id} className="flex items-center gap-2">
              <span className="font-mono text-[9px] w-4 text-right flex-shrink-0" style={{ color: 'rgba(212,197,169,0.3)' }}>{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="font-mono text-[10px] uppercase tracking-widest truncate" style={{ color: '#d4c5a9' }}>{r.content_title}</span>
                  <span className="font-mono text-[9px] flex-shrink-0 ml-2" style={{ color: 'rgba(212,197,169,0.4)' }}>{r.count}</span>
                </div>
                <div className="h-1 rounded-full overflow-hidden" style={{ background: `${color}10` }}>
                  <div className="h-full rounded-full" style={{ width: `${(r.count / max) * 100}%`, background: color }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
